import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApiFormAuthProvider, useApiListAuthProviderLink } from '../../../../../api-new/state/auth'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { IconProvider } from '../../../../../shared/icon/icon'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { useApiFormLogin } from '../../../../../api-new/state/content'
import { useLoad } from '../../../../../shared/hooks/load'
import { useLoginSysAdmin } from '../../../../../role/hook/login-sys-admin'
import { FleetPage, FleetSection, EmptyState, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { serverMessage } from '../../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import '../../../../../shared/sso-consent/sso-consent.scss'

const domainOf = email => (email && email.includes('@') ? email.slice(email.lastIndexOf('@') + 1) : '')

// Impersonate through SSO, in the workspace's language (audit F23, section
// 4): the connections this person would be sent to (the same lookup the
// sign-in page makes with their full email, so a connection in testing
// lists when they're on its allowlist), each a row with its logo and a
// "Sign in as" button that opens the identity provider in a new tab. With
// none, the page says why that can happen and where to fix it. Only system
// admins can impersonate (the auth service refuses anyone else), so others
// are told so instead of offered a button that fails.
export const LoginImpersonateSSO = () => {
  const { loginID } = useParamsInt()
  const isSysAdmin = useLoginSysAdmin()

  const apiFormLogin = useApiFormLogin()
  const apiListAuthProviderLink = useApiListAuthProviderLink()
  const apiFormAuthProvider = useApiFormAuthProvider()

  const [email, setEmail] = useState('')
  const [connections, setConnections] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [opening, setOpening] = useState(null)

  useLoad(async () => {
    setConnections(undefined)
    let login
    try {
      login = await apiFormLogin.get(loginID)
    } catch (err) {
      setConnections(null)
      return
    }
    const address = ((login && login.email) || '').trim()
    setEmail(address)
    if (!address) { setConnections([]); return }
    try {
      // The full email, so a connection still in testing lists here when
      // this person is on its test allowlist (audit B2).
      setConnections((await apiListAuthProviderLink.get(encodeURIComponent(address))) || [])
    } catch (err) {
      setConnections(null)
    }
  }, [loginID])

  const handleImpersonate = async provider => {
    setError(null)
    setOpening(provider.identifier)
    try {
      const passiveToken = await apiFormAuthProvider.post()

      // H28: the impersonator's access token is NOT passed in the URL. The
      // authenticated POST above already sent our JWT in the Authorization
      // header; the auth service stashes it against this passive login and
      // recovers it at the impersonate hop, keeping the privileged token out
      // of access logs, the Referer header and browser history.
      const params = new URLSearchParams({
        token: passiveToken.token,
        'redirect-signin-url': `${process.env.REACT_APP_SITE}/open/auth`,
        'redirect-cancel-url': `${process.env.REACT_APP_SITE}/auth/email`,
        'redirect-error-url': `${process.env.REACT_APP_SITE}/auth/error`,
        loginID: String(loginID)
      })

      window.open(`${process.env.REACT_APP_API_AUTH}/api/v1/auth-provider/${encodeURIComponent(provider.identifier)}/impersonate?${params}`, '_blank', 'noopener, noreferrer')
    } catch (err) {
      setError(serverMessage(err, `Signing in as ${email} through ${provider.displayName} couldn't start.`), err)
    } finally {
      setOpening(null)
    }
  }

  const domain = domainOf(email)

  return (
    <BreadcrumbView Icon={IconProvider} text="impersonate SSO">
      <FleetPage
        title="Sign in as this person through single sign-on"
        purpose={email
          ? `Opens ${email}'s identity provider in a new tab and signs you in as them, the way they would sign in.`
          : 'Opens their identity provider in a new tab and signs you in as them, the way they would sign in.'}>
        {!isSysAdmin &&
          <Notice tone="warn"><p>Only system administrators can sign in as someone else.</p></Notice>}

        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}

        <FleetSection id="connections" title="Their sign-in connections"
          lede={domain ? `The connections someone at ${domain} is sent to, including any in testing that list ${email}.` : undefined}>
          {connections === undefined && <p className="sw-hint m-0" role="status">Finding their connections…</p>}
          {connections === null && <p className="text-danger small m-0" role="alert">Their connections couldn't be found. Refresh the page to try again.</p>}
          {Array.isArray(connections) && connections.length === 0 &&
            <EmptyState action={<Link className="btn sw-btn" to={domain ? `/admin/provider/domains?domain=${encodeURIComponent(domain)}` : '/admin/provider/domains'}>Check the domain</Link>}>
              {email
                ? `Nobody at ${domain} is sent to single sign-on right now, so there's nothing to sign in through. The domain may not be bound to an active connection, may not be verified, or its organization may not have single sign-on in its plan.`
                : "This person has no email address, so there's no connection to look up."}
            </EmptyState>}
          {Array.isArray(connections) && connections.length > 0 &&
            <ul className="sc-rows">
              {connections.map(provider =>
                <li key={provider.identifier} className="sc-row" data-testid={`connection-${provider.identifier}`}>
                  <ProviderLogo iconSVG={provider.iconSVG} />
                  <div className="sc-row-main">
                    <span className="fw-semibold">{provider.displayName}</span>
                    <span className="sw-hint sw-mono">{provider.identifier}</span>
                  </div>
                  {isSysAdmin &&
                    <button type="button" className="btn sw-btn sw-btn-primary" disabled={opening === provider.identifier} onClick={() => handleImpersonate(provider)}>
                      {opening === provider.identifier ? 'Opening…' : 'Sign in as them'}<span className="visually-hidden"> through {provider.displayName}</span>
                    </button>}
                </li>)}
            </ul>}
        </FleetSection>
      </FleetPage>
    </BreadcrumbView>
  )
}
